import { OptionsController } from '@web3modal/core';
import { defaultWagmiConfig } from '@web3modal/wagmi';
import { createWeb3Modal } from '@web3modal/wagmi/react';
import type { Chain } from '@wagmi/core/chains';
import { fallback, http } from 'viem';

const BOXBET_MAINNET_CHAIN: Chain = {
  id: 369,
  name: 'PulseChain Mainnet',
  nativeCurrency: { name: 'Pulse', symbol: 'PLS', decimals: 18 },
  rpcUrls: {
    default: { http: ['https://rpc.pulsechain.com'] },
    public: { http: ['https://rpc.pulsechain.com'] },
  },
  blockExplorers: {
    default: { name: 'PulseScan', url: 'https://scan.pulsechain.com' },
  },
};

const BOXBET_TESTNET_CHAIN: Chain = {
  id: 943,
  name: 'PulseChain Testnet V4',
  nativeCurrency: { name: 'Pulse', symbol: 'tPLS', decimals: 18 },
  rpcUrls: {
    default: { http: ['https://rpc.v4.testnet.pulsechain.com'] },
    public: { http: ['https://rpc.v4.testnet.pulsechain.com'] },
  },
  blockExplorers: {
    default: { name: 'PulseScan Testnet', url: 'https://scan.v4.testnet.pulsechain.com' },
  },
  testnet: true,
};

const BOXBET_WAGMI_CHAINS = [BOXBET_MAINNET_CHAIN, BOXBET_TESTNET_CHAIN] as const;

const BOXBET_WALLETCONNECT_PROJECT_ID = '0b05488dcd33a3130e5f231a1d44d6f3';

const BOXBET_METADATA = {
  name: 'BoxBet',
  description: 'BoxBet markets on PulseChain.',
  url: typeof window === 'undefined' ? 'https://dextop.pro/boxbet' : window.location.origin,
  icons: [typeof window === 'undefined' ? 'https://dextop.pro/favicon.ico' : `${window.location.origin}/favicon.ico`],
};

const W3M_LIGHT_MODAL_OVERRIDES: Record<string, string> = {
  '--w3m-accent': '#000000',
  '--wui-color-accent-100': '#000000',
  '--wui-color-accent-glass-010': 'rgba(0, 0, 0, 0.08)',
  '--wui-color-modal-bg': '#ffffff',
  '--wui-color-bg-100': '#ffffff',
  '--wui-color-bg-125': '#ffffff',
  '--wui-color-bg-150': '#ffffff',
  '--wui-color-bg-175': '#ffffff',
  '--wui-color-bg-200': '#ffffff',
  '--wui-color-fg-100': '#000000',
  '--wui-color-fg-150': '#000000',
  '--wui-color-fg-200': '#000000',
  '--wui-color-fg-250': '#000000',
  '--wui-color-fg-275': '#000000',
  '--wui-color-fg-300': '#000000',
  '--wui-color-inverse-000': '#000000',
  '--wui-color-inverse-100': '#000000',
  '--wui-color-gray-glass-002': '#ffffff',
  '--wui-color-gray-glass-005': '#ffffff',
  '--wui-color-gray-glass-010': '#ffffff',
  '--wui-color-gray-glass-015': '#000000',
  '--wui-color-gray-glass-020': '#ffffff',
  '--wui-button-primary-bg': '#ffffff',
  '--wui-button-primary-text': '#000000',
  '--wui-button-primary-border': '#000000',
  '--wui-button-accent-bg': '#ffffff',
  '--wui-button-accent-text': '#000000',
  '--wui-button-accent-border': '#000000',
};

const W3M_THEME_VARIABLES = {
  '--w3m-color-mix': '#121416',
  '--w3m-color-mix-strength': 40,
  '--w3m-accent': '#ffffff',
  '--wui-color-accent-100': '#ffffff',
  '--w3m-font-family': 'Orbitron, sans-serif',
  '--w3m-border-radius-master': '0.5px',
  ...W3M_LIGHT_MODAL_OVERRIDES,
};

const WC_FEATURED_WALLET_IDS = [
  '971e689d0a5be527bac79629b4ee9b925e82208e5168b733496a09c0faed0709',
  'dd43441a6368ec9046540c46c5fdc58f79926d17ce61a176444568ca7c970dcd',
];

const WC_EXCLUDED_WALLET_IDS = [
  '8a0ee50d1f22f6651afcae7eb4253e52a3310b90af5daef78a8c4929a9bb99d4',
  'c57ca95b47569778a828d19178114f4db188b89b763c899ba0be274e97267d96',
  'd0ca99ff52b99abc48743dad0f7fc891e041be73574f7fac4afe5d4bb83845c8',
  'fd20dc426fb37566d803205b19bbc1d4096b248ac04548e3cfb6b3a38bd033aa',
];

const WC_CUSTOM_WALLETS = [
  {
    id: '971e689d0a5be527bac79629b4ee9b925e82208e5168b733496a09c0faed0709',
    name: 'OKX Wallet',
    homepage: 'https://www.okx.com/web3',
    image_id: 'b011bd5a-a887-44e2-12b0-39efbd9e5600',
    mobile_link: 'okex://main',
    app_store: 'https://apps.apple.com/us/app/okx-buy-bitcoin-eth-crypto/id1327268470',
    play_store: 'https://play.google.com/store/apps/details?id=com.okinc.okex.gp',
    rdns: 'com.okex.wallet',
  },
  {
    id: 'dd43441a6368ec9046540c46c5fdc58f79926d17ce61a176444568ca7c970dcd',
    name: 'Internet Money',
    homepage: 'https://internetmoney.io',
    image_id: '204b2240-5ce4-4996-6ec4-f06a22726900',
    mobile_link: 'internetmoney://',
    webapp_link: 'https://internetmoney.io',
    app_store: 'https://apps.apple.com/us/app/id1641771042',
    play_store: 'https://play.google.com/store/apps/details?id=com.internetmoneywallet.app',
  },
  {
    id: 'keystone-qr-hardware',
    name: 'Keystone (QR hardware)',
    homepage: 'https://keyst.one/',
    image_url: 'https://keyst.one/favicon.ico',
    mobile_link: 'keystone://walletconnect',
    webapp_link: 'https://keyst.one/',
    rdns: 'pro.keystone.wallet',
  },
];

const wagmiTransport = fallback([
  http('https://rpc-pulsechain.g4mm4.io', { timeout: 8_000, retryCount: 0 }),
  http('https://rpc.pulsechainstats.com', { timeout: 8_000, retryCount: 0 }),
  http('https://pulsechain-rpc.publicnode.com', { timeout: 8_000, retryCount: 0 }),
  http('https://rpc.pulsechain.com', { timeout: 8_000, retryCount: 0 }),
], {
  retryCount: 2,
});

export const wagmiConfig = defaultWagmiConfig({
  chains: BOXBET_WAGMI_CHAINS as unknown as [Chain, ...Chain[]],
  projectId: BOXBET_WALLETCONNECT_PROJECT_ID,
  metadata: BOXBET_METADATA,
  ssr: false,
  transports: {
    369: wagmiTransport,
    943: http('https://rpc.v4.testnet.pulsechain.com', { timeout: 8_000, retryCount: 1 }),
  } as any,
});

export const reownModal = createWeb3Modal({
  wagmiConfig,
  projectId: BOXBET_WALLETCONNECT_PROJECT_ID,
  defaultChain: BOXBET_TESTNET_CHAIN,
  allowUnsupportedChain: false,
  featuredWalletIds: WC_FEATURED_WALLET_IDS,
  excludeWalletIds: WC_EXCLUDED_WALLET_IDS,
  customWallets: WC_CUSTOM_WALLETS,
  enableAnalytics: true,
  enableOnramp: false,
  themeMode: 'light',
  themeVariables: W3M_THEME_VARIABLES,
});

OptionsController.setFeaturedWalletIds(WC_FEATURED_WALLET_IDS);
OptionsController.setExcludeWalletIds(WC_EXCLUDED_WALLET_IDS);
OptionsController.setCustomWallets(WC_CUSTOM_WALLETS as never);

export function applyReownLightMode(): void {
  if (typeof document === 'undefined') return;

  reownModal.setThemeMode('light');
  reownModal.setThemeVariables(W3M_THEME_VARIABLES);

  const modalHosts = Array.from(document.querySelectorAll<HTMLElement>('w3m-modal'));
  modalHosts.forEach((host) => {
    Object.entries(W3M_LIGHT_MODAL_OVERRIDES).forEach(([cssVar, cssValue]) => {
      host.style.setProperty(cssVar, cssValue);
    });
  });
}

if (typeof window !== 'undefined' && typeof document !== 'undefined') {
  const sync = () => applyReownLightMode();
  sync();
  queueMicrotask(sync);
  requestAnimationFrame(sync);

  const observer = new MutationObserver(sync);
  observer.observe(document.body, { childList: true, subtree: true });
}
